import React, { useEffect, useRef, useState } from 'react';
import {
  UniteUsProgramSearchResults,
  UniteUsService,
} from '@src/api.types';
import { TMemberInfo } from '@repo/utils';
import { CboSearchParams } from '@src/routes/SearchImproved/SearchImproved';
import { LoadingSpinnerModal } from '../LoadingSpinnerModal/LoadingSpinnerModal';
import { CboSubcategoryModal } from '../CboCategories/CboSubcategoryModal';
import {
  CategoryData,
  CboCategoriesPanel,
  coerceUniteUsCategories,
} from '../CboCategories/CboCategories';
import { API } from '@src/api';
import { CboCardUniteUs, UniteUsFilterPanel, UniteUsProgram } from '@repo/ui';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { CboSearchPaginationTopBar } from '../CboSearchPaginationTopBar/CboSearchPaginationTopBar';
import {
  CboSearchCategoryBreadcrumbs,
  getBreadcrumbTrail,
  uniteusCatsToGeneric,
} from '../CboSearchCategoryBreadcrumbs/CboSearchCategoryBreadcrumbs';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { BulkReferDropdownButton } from '@repo/ui/src/BulkReferDropdownButton';
import { getBulkReferral, getFeatureFlagsFromStorage, setBulkReferral } from '@src/utills/storage';
import { CboMap, uniteusIntoMarkerTarget } from '../CboMap/CboMap';
import { CboReferralSubmissionResults } from '../CboReferralSubmissionResults/CboReferralSubmissionResults';

import { CboSearchNoResultsFound } from '../CboSearchNoResultsFound/CboSearchNoResultsFound';
import { findRecursive } from '@src/utills/utils';
import { FeatureFlags } from '../../../../../packages/utils/src/schemas/flagSmith';
import { ReferralFormModal } from '../ReferralForm/ReferralForm';
import { useAtomValue } from 'jotai';
import { EmailMember } from '../Email/EmailMember';

import { loggedInAdvocateAtom } from '@src/global-state';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { printUniteUs } from '@src/utills/printProgram';
import { UniteUsFilter, ApiResponse } from '@src/api.types';
import { CboDetailModal } from '../CboDetailsModal/CboDetailsModal';
import { CboMapAzure } from '../CboMap/CboMapAzure';
export type CboSearchWrapperUniteUsProps = {
  azureMapsApiKey: string;
  searchParameters: CboSearchParams;
  setSearchParameters: (next: CboSearchParams) => void;
  member: TMemberInfo;
  vendor: 'findhelp' | 'Unite Us';
  favoritesFolderNames: string[];
  favoritedProgramIds: Set<string>;
  onSaveFavorite: (folderName: string, programId: string) => void;
  onRemoveFavorite: (programId: string) => void;
  currentZipcode?: string;
};

export function CboSearchWrapperUniteUs({
  azureMapsApiKey,
  searchParameters,
  setSearchParameters,
  member,
  vendor,
  currentZipcode,
}: CboSearchWrapperUniteUsProps) {
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const bulkReferralFeature = false;
  const [bulkReferring, setBulkReferring] = useState<any[]>(() => getBulkReferral());

  // Track previous vendor to guard against stale category on vendor switch
  const prevVendorRef = useRef<string | undefined>(vendor);
  const [showDetailsFor, setShowDetailsFor] = useState<
    UniteUsProgram | undefined
  >(undefined);
  const [networkHubSupportProgram, setNetworkHubSupportProgram] = useState<
    UniteUsProgram[] | undefined
  >(undefined);

  const [referResponse, setReferResponse] = useState<
    ApiResponse<any> | null | undefined
  >(undefined);
  // Controls referral modal visibility
  const [referringTo, setReferringTo] = useState<any | undefined>(undefined);
  // Print functionality removed in V2 migration - handled directly via printUniteUs().

  const [categoryData, setCategoryData] = useState<
    UniteUsService[] | undefined
  >([]);
  const [filterData, setFilterData] = useState<UniteUsFilter[] | undefined>(
    undefined
  );
  // Undefined = no search performed
  // Null = search performed, but returned an error
  // Otherwise, it's the search results (even if they contain zero programs)
  const [programSearchResults, setProgramSearchResults] = useState<
    UniteUsProgramSearchResults | undefined | null
  >(undefined);

  const [showCategoryModalFor, setShowCategoryModalFor] = useState<
    CategoryData | undefined
  >(undefined);

  // Controls email modal visibility
  const [emailingProgram, setEmailingProgram] = useState<
    UniteUsProgram | undefined
  >(undefined);

  useEffect(() => {
    // Every time the programs in the bulk referral "cart" changes,
    // update the local storage w/ new value
    setBulkReferral(bulkReferring as any);
  }, [bulkReferring]);

  // Print functionality removed in V2 migration - handled directly via printUniteUs().

  useEffect(() => {
    async function fetchCategories() {
      setIsLoading(true);
      const resp = await API.getUniteUsCategories();

      if (
        !!resp &&
        'status' in resp &&
        resp.status === 'success' &&
        'data' in resp &&
        Array.isArray(resp.data)
      ) {
        setCategoryData(resp.data);
        setIsLoading(false);
      }
    }

    if (searchParameters.zipcode) {
      fetchCategories();
    }
  }, [searchParameters.zipcode]);

  useEffect(() => {
    async function getUniteUsFilters() {
      setIsLoading(true);
      const resp = await API.getUniteUsFilters();
      if (
        !!resp &&
        'status' in resp &&
        resp.status === 'success' &&
        'data' in resp &&
        typeof resp.data === 'object' &&
        'populations' in resp.data &&
        Array.isArray(resp.data.populations)
      ) {
        setFilterData(resp.data.populations);
        setIsLoading(false);
      }
    }

    if (filterData === undefined) {
      getUniteUsFilters();
    }
  }, []);

  useEffect(() => {
    // Only run Unite Us searches when the active vendor is Unite Us.
    if (vendor !== 'Unite Us') {
      prevVendorRef.current = vendor;
      setProgramSearchResults(undefined);
      return;
    }

    // If the vendor just switched to Unite Us, the category in searchParameters may
    // still belong to the previous vendor. Clear results and wait for a fresh category.
    if (prevVendorRef.current !== 'Unite Us') {
      prevVendorRef.current = vendor;
      setProgramSearchResults(undefined);
      return;
    }
    prevVendorRef.current = vendor;

    async function fetchPrograms() {
      if (!categoryData) {
        console.warn(
          'No category data is available, so programs search could not be performed.'
        );
        return;
      }

      setIsLoading(true);

      // Scary-looking type is just a union of the two possible API calls' awaited promises
      let resp:
        | undefined
        | Awaited<ReturnType<typeof API.searchUniteUsPrograms>> = undefined;

      // Look for a category whose label matches the search parameter
      // FIXME
      const catMatchingTerm = findRecursive(
        searchParameters.category,
        categoryData.map(uniteusCatsToGeneric)
      );

      resp = await API.searchUniteUsPrograms({
        // Get the UUID from the category that the user typed in, if there's a direct match.
        // Otherwise, send it as free-form text.
        // NOTE - This will currently break if it is free-form text, because the backend will
        //        attempt to parse the field into UUID[]. Need to revisit this contract once
        //        we have a better idea fo what we want to handle.
        categoryUuid: (catMatchingTerm as any)?.id ?? null,
        zipcode: searchParameters.zipcode,
        page: searchParameters.page,
        recordsPerPage: searchParameters.recordsPerPage,
        attributeFilters: searchParameters.filters,
        sortBy: searchParameters.sortBy,
        distanceRadius: searchParameters.distanceRadius,
        searchkeyword: (catMatchingTerm as any)?.id ? null : searchParameters.category ?? null,
      });

      if (!resp || ('status' in resp && resp.status === 'error')) {
        setProgramSearchResults(null);
        // TODO - Show feedback to user about the error
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      if (resp?.status === 'success' && (resp?.data?.results?.data || resp?.data?.network_hub_support)) {
        setNetworkHubSupportProgram(resp?.data?.network_hub_support || []);
        if (Array.isArray(resp.data.results.data)) {
          setProgramSearchResults(resp.data);
          return;
        }
      }
    }

    // Only perform the programs search if we have the two required parameters
    if (!!searchParameters.zipcode && !!searchParameters.category) {
      fetchPrograms();
    }
  }, [vendor, searchParameters, categoryData]);
  const featureFlagsFromStorage: FeatureFlags = getFeatureFlagsFromStorage();

  return (
    <React.Fragment>
      {/* Modals */}

      {
        // Loading state
        isLoading && <LoadingSpinnerModal isLoading={isLoading} />
      }
      {
        // if the user clicked a category button, show the subcategory modal
        showCategoryModalFor !== undefined && categoryData && (
          <CboSubcategoryModal
            currentTopLevelCategory={showCategoryModalFor}
            onClose={() => {
              return setShowCategoryModalFor(undefined);
            }}
            onClickCategoryFilter={(clicked: CategoryData) => {
              setShowCategoryModalFor(undefined);
              setSearchParameters({
                ...searchParameters,
                category: clicked.label,
                categoryId: clicked.id,
                filters: [],
                page: 1,
                zipcode: currentZipcode ?? '',
              });
            }}
          />
        )
      }

      {/* Top-level container */}
      <div
        style={{
          paddingLeft: '5rem',
          paddingRight: '5rem',
        }}
      >
        {/* Category / "service tags" button panel */}
        {categoryData && (
          <div
            style={{
              paddingTop: 4,
            }}
          >
            <CboCategoriesPanel
              categoryData={coerceUniteUsCategories(categoryData)}
              onClickCategoryButton={(category: CategoryData) => {
                return setShowCategoryModalFor(category);
              }}
            />
          </div>
        )}

        {
          // TODO - Checks for the program search results here
          // Pagination controls, bulk refer button, category breadcrumbs,
          // map & filter panel
          programSearchResults &&
          (programSearchResults?.results.meta.page.total_count <= 0 && (!networkHubSupportProgram || networkHubSupportProgram.length === 0) ? (
              <div style={{ paddingTop: '32px' }}>
                <CboSearchNoResultsFound
                  searchTerm={searchParameters.category}
                />
              </div>
            ) : (
              // Otherwise we have results and can show the program cards,
              // pagintation controls, map, filters, etc.
              <Grid
                style={{
                  paddingTop: '16px',
                }}
              >
                {/* Left column: Search results info, pagination, program cards */}
                <Grid.Col
                  span={{
                    xs: 12,
                    lg: 8,
                  }}
                  style={{
                    paddingBottom: 8,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {/* Top bar: pagination */}
                  <Grid.Col span={{ xs: 12 }}>
                    <CboSearchPaginationTopBar
                      totalRecords={
                        programSearchResults.results.meta.page.total_count +1
                      }
                      recordsPerPage={searchParameters.recordsPerPage}
                      currentPage={searchParameters.page}
                      onPageChange={(next: number) => {
                        setSearchParameters({
                          ...searchParameters,
                          page: next,
                        });
                      }}
                      onRecordsPerPageChange={(next: number) => {
                        setSearchParameters({
                          ...searchParameters,
                          page: 1,
                          recordsPerPage:
                            next as typeof searchParameters.recordsPerPage,
                        });
                      }}
                    />
                  </Grid.Col>

                  <Grid.Col
                    span={{ xs: 12 }}
                    style={{
                      paddingTop: 0,
                      paddingBottom: 8,
                    }}
                  >
                    <Grid>
                      <Grid.Col
                        span={{ xs: 12, sm: 6 }}
                        style={{
                          marginTop: 12,
                        }}
                      >
                        {/* Category search breadcrumbs (showing the taxonomy of category / sub-category) */}
                        {categoryData && (
                          <CboSearchCategoryBreadcrumbs
                            breadcrumbTrail={getBreadcrumbTrail(
                              searchParameters.categoryId ?? '',
                              categoryData.map(uniteusCatsToGeneric)
                            )}
                            onClickCrumb={(catname: any, catid: any) => {
                              setSearchParameters({
                                ...searchParameters,
                                category: catname,
                                categoryId: catid,
                              });
                            }}
                          />
                        )}
                      </Grid.Col>

                      <Grid.Col
                        span={{ xs: 12, sm: 6 }}
                        style={{
                          textAlign: 'right',
                        }}
                      >
                        <Flex direction="row" justify="flex-end">
                          {bulkReferralFeature && (
                            // Bulk refer button
                            <BulkReferDropdownButton
                              onClickSave={() => {
                                return window.alert('Clicked save');
                              }}
                              onClickCancel={() => {
                                return window.alert('Clicked cancel');
                              }}
                              bulkReferringLength={bulkReferring.length}
                            />
                          )}
                        </Flex>
                      </Grid.Col>
                    </Grid>
                  </Grid.Col>

                  {/* Program cards */}
                  <Grid.Col
                    span={{ xs: 12 }}
                    css={{
                      'abyss-grid-col': {
                        padding: '0px',
                      },
                    }}
                  >
                    <div
                      style={{
                        overflowY: 'scroll',
                        overflowX: 'hidden',
                        scrollbarWidth: 'thin',
                        maxHeight: '70vh',
                      }}
                      id="search-results-container"
                    >
                     {networkHubSupportProgram && networkHubSupportProgram.map((program) => {return (
                          <CboCardUniteUs
                                programData={program}
                                setReferTo={(referringTo: UniteUsProgram) => {
                                  setReferringTo(program);
                                }} // TODO
                                checked={false}
                                setPChecked={() => {}} // TODO
                                setExpandedViewProgram={(
                                ) => {}} // TODO
                                onClickService={(service: string) => {
                                  // Update the search parameters to find programs providing
                                  // the selected service.
                                  // Reset the page number to one, but keep the existing filters.
                                  setSearchParameters({
                                    ...searchParameters,
                                    category: service,
                                    page: 1,
                                  });
                                }}
                                onClickPrint={() => {
                                  printUniteUs(program);
                                }}
                                onClickTitle={() => {
                                  return setShowDetailsFor(program);
                                }}
                                onClickServingAttribute={(
                                  attribute: string
                                ) => {
                                  // Add the attribute tag to the current list of filters
                                  if (
                                    !searchParameters.filters.includes(
                                      attribute
                                    )
                                  ) {
                                    setSearchParameters({
                                      ...searchParameters,
                                      page: 1,
                                      filters: [
                                        ...searchParameters.filters,
                                        attribute,
                                      ],
                                    });
                                  }
                                }}
                                onClickEmail={() => {
                                  setEmailingProgram(program);
                                }}
                              />
                        )})}
                      {programSearchResults?.results.data.map(
                        (program, idx) => {
                          return (
                            <div
                              id={`${program.id}-card-wrapper`}
                              key={idx}
                              style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                              }}
                            >

                              {/* TODO - Put UniteUs card here */}
                              <CboCardUniteUs
                                programData={program}
                                setReferTo={(referringTo: UniteUsProgram) => {
                                  setReferringTo(program);
                                }} // TODO
                                checked={false}
                                setPChecked={() => {}} // TODO
                                setExpandedViewProgram={(
                                ) => {}} // TODO
                                onClickService={(service: string) => {
                                  // Update the search parameters to find programs providing
                                  // the selected service.
                                  // Reset the page number to one, but keep the existing filters.
                                  setSearchParameters({
                                    ...searchParameters,
                                    category: service,
                                    page: 1,
                                  });
                                }}
                                onClickPrint={() => {
                                  printUniteUs(program);
                                }}
                                onClickTitle={() => {
                                  return setShowDetailsFor(program);
                                }}
                                onClickServingAttribute={(
                                  attribute: string
                                ) => {
                                  // Add the attribute tag to the current list of filters
                                  if (
                                    !searchParameters.filters.includes(
                                      attribute
                                    )
                                  ) {
                                    setSearchParameters({
                                      ...searchParameters,
                                      page: 1,
                                      filters: [
                                        ...searchParameters.filters,
                                        attribute,
                                      ],
                                    });
                                  }
                                }}
                                onClickEmail={() => {
                                  setEmailingProgram(program);
                                }}
                              />
                            </div>
                          );
                        }
                      )}
                    </div>
                  </Grid.Col>
                </Grid.Col>

                {/* Right column: Map & filters */}
                <Grid.Col
                  span={{
                    xs: 12,
                    lg: 4,
                  }}
                  style={{
                    position: 'sticky',
                    overflowY: 'scroll',
                    scrollbarWidth: 'thin',
                    maxHeight: '75vh',
                  }}
                >
                  {(() => {
                    const markerTargets = programSearchResults?.results.data
                      .map(uniteusIntoMarkerTarget)
                      .filter((tgt): tgt is NonNullable<typeof tgt> => tgt !== undefined);

                    const handleViewProgramLink = (clickedProgramId: string) => {
                      const card = document.getElementById(
                        `${clickedProgramId}-card-wrapper`
                      );
                      if (!card) {
                        console.warn(
                          `Could not find card with id ${clickedProgramId}-card`
                        );
                        return;
                      }
                      card.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    };

                    return (featureFlagsFromStorage as any)?.googlemaps_enabled?.enabled ? (
                      <CboMap
                        apiKey={azureMapsApiKey}
                        programSearchResults={markerTargets ?? []}
                        onClickViewProgramLink={handleViewProgramLink}
                      />
                    ) : (<CboMapAzure
                        subscriptionKey={azureMapsApiKey}
                        programSearchResults={markerTargets ?? []}
                        onClickViewProgramLink={handleViewProgramLink}
                      />);
                  })()}

                  <hr
                    style={{
                      marginTop: '16px',
                      marginBottom: '16px',
                      border: '0px solid #E0E0E0',
                    }}
                  />

                  {filterData === undefined ? (
                    <LoadingSpinner
                      size="xl"
                      isLoading={true}
                      ariaLoadingLabel="Downloading filters"
                    />
                  ) : (
                    <UniteUsFilterPanel
                      populationFiltersList={filterData as any}
                      currentPopulationRestrictions={[]}
                      currentPopulationServing={searchParameters.filters}
                      setPopulationRestrictions={(
                      ) => {}}
                      setPopulationServing={(populationServing: string[]) => {
                        setSearchParameters({
                          ...searchParameters,
                          filters: populationServing,
                        });
                      }}
                      distanceRadius={searchParameters.distanceRadius as any}
                      setDistanceRadius={(radius) => {
                        setSearchParameters({
                          ...searchParameters,
                          distanceRadius: radius,
                        });
                      }}
                      sortBy={searchParameters.sortBy}
                      setSortBy={(next) => {
                        setSearchParameters({
                          ...searchParameters,
                          sortBy: next,
                        });
                      }}
                    />
                  )}
                </Grid.Col>
              </Grid>
            ))
        }

        {referringTo !== undefined && (
          <ReferralFormModal
            vendor="uniteus"
            referringTo={referringTo}
            onClose={() => {
              setReferringTo(undefined);
            }}
            setReferResponse={setReferResponse}
            memberInContext={member as any}
            paCoalitionConsent={
              (featureFlagsFromStorage as any)?.agent_pennsylvania_coalition_consent
                ?.enabled || false
            }
            loggedInAdvocate={loggedInAdvocate!}
            zipcode={searchParameters.zipcode}
          />
        )}
        {
          // Submitted a referral, show the user a pop-up on success or failure
          referResponse !== undefined && (
            <CboReferralSubmissionResults
              referResp={referResponse}
              setReferResp={setReferResponse}
              fromBulkRefer={undefined}
              setBulkReferResp={() => {}}
            />
          )
        }
        {
          // User clicked a program card, show the details modal
          showDetailsFor !== undefined && (
            <CboDetailModal
              vendor="uniteus"
              isOpen={showDetailsFor !== undefined}
              program={showDetailsFor}
              onClose={() => {
                return setShowDetailsFor(undefined);
              }}
              isPrinting={false}
            />
          )
        }

        {
          // Show the email modal if the user clicks the email button on a program card
          emailingProgram !== undefined && (
            <EmailMember
              vendor="uniteus"
              programData={emailingProgram}
              isOpen={emailingProgram !== undefined}
              setIsOpen={(next: boolean) => {
                if (!next) setEmailingProgram(undefined);
              }}
            />
          )
        }
      </div>
    </React.Fragment>
  );
}